import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Backdrop, Card, CardActionArea, CircularProgress, Divider, List, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import { connect } from 'react-redux';
import { refreshSession } from '../Actions/thunks';
import { useEffect, useState } from 'react';
import { ClaimStatus, Property, PropertyOwnershipClaim } from '../Models/Property';
import { baseEndpoint } from '../App';
import {
    Schedule,
    CheckCircle,
    Update,
    CompareArrows,
    Dashboard as DashboardIcon,
    Home as HomeIcon,
    Payment as PaymentIcon,
    Folder as FolderIcon,
    Settings as SettingsIcon
} from '@material-ui/icons';
import LandlordClaimsView from '../Dashboards/LandlordClaimsView/LandlordClaimsView';
import { Subscription, Plan } from '../Models/Subscriptions';
import clsx from 'clsx';
import { Answer } from '../Models/Info';
import { Question } from '../Models/Info';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flexGrow: 1,
            margin: "15px",
            textAlign: "left"
        },
        menuButton: {
            marginRight: theme.spacing(2),
        },
        title: {
            flexGrow: 1,
        },
        downloadButton: {
            backgroundColor: "#17d703",
            color: "white",
            borderRadius: "35px",
            textTransform: "none",
            padding: "2px 12px",  // reduced vertical padding
            fontSize: "12px",
            minWidth: "auto",
            height: "24px",       // explicit height for slimmer look
            lineHeight: 1,        // helps center text vertically
            marginLeft: "auto",   // pushes button to the right
            '&:hover': {
                backgroundColor: "#15c003"
            }
        },
        downloadIcon: {
            fontSize: 14  // smaller icon
        },
        listItemContent: {
            display: "flex",
            width: "100%",
            alignItems: "center"
        },
        statusIcon: {
            marginRight: theme.spacing(1),
        },
        statusPending: {
            color: theme.palette.warning.main, // Usually orange
        },
        statusAccepted: {
            color: '#17d703', // Matching your green
        },
        statusUpdateRequired: {
            color: theme.palette.error.main, // Usually red
        },
        statusSection: {
            marginBottom: theme.spacing(3),
        },
        statusHeading: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: theme.spacing(1),
            paddingLeft: theme.spacing(2),
        },
        propertyCard: {
            margin: theme.spacing(1),
            '&:hover': {
                boxShadow: theme.shadows[4]
            }
        },
        cardContent: {
            display: 'flex',
            padding: theme.spacing(1.5),
            alignItems: 'center',
            minHeight: "85px"
        },
        sectionDivider: {
            margin: theme.spacing(3, 0),
            backgroundColor: '#f0f0f0',  // light grey
            height: '2px'
        },
        propertyImage: {
            maxHeight: '85px',
            width: '85px',
            objectFit: 'cover'
        },
        backdrop: {
            zIndex: theme.zIndex.drawer + 1,
            color: '#fff',
            backgroundColor: 'rgba(0, 0, 0, 0.7)'
        },
        loadingContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(2)
        },
        loadingText: {
            color: '#fff',
            marginTop: theme.spacing(2)
        },
        unitContainer: {
            display: 'flex',
            gap: '4px',
            flexWrap: 'wrap',
            marginTop: '10px'
        },
        unitBox: {
            width: '24px',
            height: '24px',
            borderRadius: '4px',
            margin: '2px'
        },
        unitFilled: {
            backgroundColor: '#17d703' // green for claimed units
        },
        unitMinimum: {
            backgroundColor: '#ff9800' // orange for minimum required
        },
        unitAvailable: {
            backgroundColor: '#e0e0e0' // grey for available slots
        },
        unitMaxed: {
            backgroundColor: '#f44336' // red for over maximum
        },
        claimButton: {
            backgroundColor: '#17d703',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '25px',
            marginTop: '20px',
            '&:hover': {
                backgroundColor: '#15c003'
            }
        },
        legend: {
            display: 'flex',
            gap: '20px',
            marginTop: '10px',
            alignItems: 'center'
        },
        legendItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        pageContainer: {
            minHeight: '100vh',
            backgroundColor: '#f5f5f5'
        },
        dashboardContainer: {
            display: 'flex',
            position: 'relative',
            margin: '0 auto',
            maxWidth: '1920px' // Optional: prevents extreme stretching on wide screens
        },
        leftNav: {
            width: '250px',
            backgroundColor: 'white',
            borderRight: '1px solid #e0e0e0',
            padding: theme.spacing(2),
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflowY: 'auto',
            '& .MuiListItem-root': {
                borderRadius: theme.spacing(1),
                marginBottom: theme.spacing(1),
            },
            '& .MuiListItem-root.Mui-selected': {
                backgroundColor: '#f0f0f0',
                '&:hover': {
                    backgroundColor: '#e0e0e0'
                }
            }
        },
        mainContent: {
            flex: 1,
            padding: theme.spacing(3),
            minHeight: '100vh'
        },
        navItem: {
            display: 'flex',
            alignItems: 'center',
            padding: theme.spacing(1.5),
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: '#f5f5f5',
                borderRadius: theme.spacing(1)
            }
        },
        navIcon: {
            marginRight: theme.spacing(2),
            color: '#666'
        },
        activeNavItem: {
            backgroundColor: '#f0f0f0',
            '&:hover': {
                backgroundColor: '#e0e0e0'
            }
        }
    }),
);


export interface LandlordDashboardProps {
    userState: any,
    refreshSession: () => void
}

function LandlordDashboard(props: LandlordDashboardProps) {
    const config = require("../env.json")
    const [streetViewUrls, setStreetViewUrls] = useState<string[]>([]);
    const [propertiesOwned, setPropertiesOwned] = useState<{ property: Property, claim: PropertyOwnershipClaim }[]>(
        [] as { property: Property, claim: PropertyOwnershipClaim }[]
    )
    const classes = useStyles();
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [selectedClaim, setSelectedClaim] = useState<PropertyOwnershipClaim | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [subscriptionLoading, setSubscriptionLoading] = useState<boolean>(false);
    const [openPlanDialog, setOpenPlanDialog] = useState(false);
    const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [activeSection, setActiveSection] = useState('dashboard');
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [changingPlan, setChangingPlan] = useState(false);

    const navigationItems = [
        { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
        { id: 'properties', label: 'My Properties', icon: <HomeIcon /> },
        { id: 'subscription', label: 'Subscription', icon: <PaymentIcon /> },
        { id: 'documents', label: 'Documents', icon: <FolderIcon /> },
        { id: 'settings', label: 'Settings', icon: <SettingsIcon /> }
    ];

    useEffect(() => {
        fetchPropertiesOwned();
        fetchSubscription();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const handleCardClick = async (property: Property, claim: PropertyOwnershipClaim) => {
        setLoading(true);
        const requestOptions = {
            headers: {
                "Authorization": "Bearer " + props.userState.user.currentToken,
                'Content-Type': 'application/json'
            }
        };

        try {
            // Fetch the latest claim data
            const updatedClaim = await fetchClaimById(claim.internalId.toString());
            if (!updatedClaim) return;

            const response = await fetch(baseEndpoint + `properties/address/${claim.addressId}`, requestOptions);
            if (response.ok) {
                updatedClaim.property = await response.json();
                setSelectedClaim(updatedClaim);
                setOpenDialog(true);
            } else {
                console.error('Failed to fetch claim details');
            }
        } catch (error) {
            console.error('Error fetching claim details:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchClaimById = async (internalId: string) => {
        const requestOptions = {
            headers: {
                "Authorization": "Bearer " + props.userState.user.currentToken,
                'Content-Type': 'application/json'
            }
        };

        try {
            const response = await fetch(baseEndpoint + `propertyownershipclaims/${internalId}`, requestOptions);
            if (response.ok) {
                const claim: PropertyOwnershipClaim = await response.json();
                return claim;
            } else {
                console.error('Failed to fetch claim');
                return null;
            }
        } catch (error) {
            console.error('Error fetching claim:', error);
            return null;
        }
    };

    async function fetchSubscription() {
        setSubscriptionLoading(true);
        try {
            const requestOptions = {
                headers: {
                    "Authorization": "Bearer " + props.userState.user.currentToken,
                    'Content-Type': 'application/json'
                }
            };

            const response = await fetch(
                `${baseEndpoint}users/subscription/${props.userState.user.internalId}`, 
                requestOptions
            );

            if (response.ok) {
                const data = await response.json();
                setSubscription(data);
            } else {
                console.error('Failed to fetch subscription');
            }
        } catch (error) {
            console.error('Error fetching subscription:', error);
        } finally {
            setSubscriptionLoading(false);
        }
    }


    async function fetchPropertiesOwned() {
        try {
            var requestOptions = {
                headers: {
                    "Authorization": "Bearer " + props.userState.user.currentToken,
                    'Content-Type': 'application/json'
                }
            }
            await fetch(baseEndpoint + 'properties/claimed/all', requestOptions).then(async (response) => {
                if (response.ok) {
                    var result = await response.json()
                    if (result.properties.length > 0) {
                        var urls = [] as string[]
                        var propClaims = [] as { property: Property, claim: PropertyOwnershipClaim }[]
                        result.properties.map((p: Property, i: number) => {
                            const streetViewLat = p.addresses[0].streetLat
                            const streetViewLng = p.addresses[0].streetLng
                            urls.push("https://maps.googleapis.com/maps/api/streetview?size=85x85&location=" +
                                streetViewLat + "," + streetViewLng + "&source=outdoor&heading=" + p.addresses[0].heading +
                                "&fov=90&&key=" + config.GOOGLE_API_KEY);
                            console.log(result.claims.find((c: PropertyOwnershipClaim) => c.addressId === p.addresses[0].internalId))
                            propClaims.push({ property: p, claim: result.claims.find((c: PropertyOwnershipClaim) => c.addressId === p.addresses[0].internalId) })
                            return null;

                        })
                        setStreetViewUrls(urls);
                        setPropertiesOwned(propClaims);
                    }


                }
            })
        }
        catch (ex) {

        }
    }

    const renderStatusIcon = (status: ClaimStatus) => {
        switch (status) {
            case ClaimStatus.Pending:
                return <Schedule className={`${classes.statusIcon} ${classes.statusPending}`} />;
            case ClaimStatus.Accepted:
                return <CheckCircle className={`${classes.statusIcon} ${classes.statusAccepted}`} />;
            case ClaimStatus.Rejected:
                return <Update className={`${classes.statusIcon} ${classes.statusUpdateRequired}`} />;
        }
    };
    const groupedProperties = propertiesOwned.reduce((acc, { property, claim }) => {
        const status = claim?.status ?? ClaimStatus.Pending;
        if (!acc[status]) {
            acc[status] = [];
        }
        acc[status].push({ property, claim });
        return acc;
    }, {} as Record<ClaimStatus, typeof propertiesOwned>);

    const handleClaimUpdated = (updatedClaim: PropertyOwnershipClaim) => {
        // Update the claim in your list of properties
        const updatedProperties = propertiesOwned.map(prop => {
            if (prop.claim.internalId === updatedClaim.internalId) {
                return { ...prop, claim: updatedClaim };
            }
            return prop;
        });
        setPropertiesOwned(updatedProperties);
    };

    const fetchAvailablePlans = async () => {
        setLoadingPlans(true);
        try {
            const response = await fetch(`${baseEndpoint}subscription/plans/landlord`, {
                headers: {
                    "Authorization": "Bearer " + props.userState.user.currentToken,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const plans = await response.json();
                setAvailablePlans(plans);
            } else {
                throw new Error('Failed to fetch plans');
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
        } finally {
            setLoadingPlans(false);
        }
    };

    const handlePlanChange = async (newPlanId: string) => {
        try {
            const response = await fetch(`${baseEndpoint}users/subscription/change`, {
                method: 'POST',
                headers: {
                    "Authorization": "Bearer " + props.userState.user.currentToken,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ planId: newPlanId })
            });

            if (response.ok) {
                // Refresh subscription data
                await fetchSubscription();
                setOpenPlanDialog(false);
            }
        } catch (error) {
            console.error('Error changing plan:', error);
        }
    };

    const handlePlanSelection = (plan: Plan) => {
        setSelectedPlan(plan);
        setConfirmDialogOpen(true);
    };

    const handleConfirmPlanChange = async () => {
        if (!selectedPlan) return;
        
        setChangingPlan(true);
        try {
            await handlePlanChange(selectedPlan.catalogId);
            setConfirmDialogOpen(false);
            setOpenPlanDialog(false);
        } catch (error) {
            console.error('Error changing plan:', error);
        } finally {
            setChangingPlan(false);
            setSelectedPlan(null);
        }
    };

    const renderSubscriptionInfo = () => {
        if (subscriptionLoading) {
            return (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <CircularProgress />
                </div>
            );
        }

        if (!subscription) {
            return null;
        }

        const renderUnitBoxes = () => {
            const boxes = [];
            const totalUnits = Math.max(subscription.maxUnits, subscription.currentUnits);
            
            for (let i = 0; i < totalUnits; i++) {
                let boxClass = classes.unitBox;
                if (i < subscription.currentUnits) {
                    boxClass += ` ${classes.unitFilled}`;
                } else if (i < subscription.minUnits) {
                    boxClass += ` ${classes.unitMinimum}`;
                } else if (i < subscription.maxUnits) {
                    boxClass += ` ${classes.unitAvailable}`;
                } else {
                    boxClass += ` ${classes.unitMaxed}`;
                }
                boxes.push(<div key={i} className={boxClass} />);
            }
            return boxes;
        };

        return (
            <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" gutterBottom>
                        Subscription Details
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<CompareArrows />}
                        onClick={() => {
                            fetchAvailablePlans();
                            setOpenPlanDialog(true);
                        }}
                        style={{ marginBottom: '8px' }}
                    >
                        Change Plan
                    </Button>
                </div>
                <Paper elevation={2} style={{ padding: '20px', marginTop: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                        <div>
                            <Typography variant="h6">{subscription.title}</Typography>
                            <Typography color="textSecondary">{subscription.description}</Typography>
                        </div>
                        <Typography 
                            variant="h6" 
                            style={{ 
                                color: subscription.status === 'ACTIVE' ? '#17d703' : 'orange'
                            }}
                        >
                            {subscription.status}
                        </Typography>
                    </div>

                    <Divider style={{ margin: '15px 0' }} />

                    <Typography variant="subtitle1" gutterBottom>
                        Property Units ({subscription.currentUnits} claimed)
                    </Typography>
                    
                    <div className={classes.unitContainer}>
                        {renderUnitBoxes()}
                    </div>

                    <div className={classes.legend}>
                        <div className={classes.legendItem}>
                            <div className={`${classes.unitBox} ${classes.unitFilled}`} />
                            <Typography variant="body2">Claimed</Typography>
                        </div>
                        <div className={classes.legendItem}>
                            <div className={`${classes.unitBox} ${classes.unitMinimum}`} />
                            <Typography variant="body2">Included in Plan ({subscription.minUnits})</Typography>
                        </div>
                        <div className={classes.legendItem}>
                            <div className={`${classes.unitBox} ${classes.unitAvailable}`} />
                            <Typography variant="body2">Additional Available</Typography>
                        </div>
                        {subscription.currentUnits > subscription.maxUnits && (
                            <div className={classes.legendItem}>
                                <div className={`${classes.unitBox} ${classes.unitMaxed}`} />
                                <Typography variant="body2">Upgrade Needed</Typography>
                            </div>
                        )}
                    </div>

                    {subscription.currentUnits < subscription.minUnits && (
                        <Typography 
                            variant="body2" 
                            style={{ 
                                marginTop: '10px',
                                color: '#ff9800'  // orange to match the boxes
                            }}
                        >
                            Pro tip: Your plan includes {subscription.minUnits} properties - claim {subscription.minUnits - subscription.currentUnits} more to maximize your subscription!
                        </Typography>
                    )}

                    <Divider style={{ margin: '15px 0' }} />
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                            <Typography variant="body2" color="textSecondary">
                                Price Per Unit
                            </Typography>
                            <Typography variant="body1">
                                ${(subscription.pricePerUnit / 100).toFixed(2)}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="body2" color="textSecondary">
                                Current Monthly Price
                            </Typography>
                            <Typography variant="body1">
                                ${(subscription.currentPrice / 100).toFixed(2)}
                            </Typography>
                        </div>
                    </div>

                    <Button
                        variant="contained"
                        className={classes.claimButton}
                        onClick={() => window.location.href = '/properties'}
                        fullWidth
                    >
                        Claim New Property
                    </Button>
                </Paper>
            </div>
        );
    };

    const renderPlanSelectionDialog = () => (
        <Dialog
            open={openPlanDialog}
            onClose={() => setOpenPlanDialog(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Typography variant="h5">
                    Change Subscription Plan
                </Typography>
            </DialogTitle>
            <DialogContent>
                {loadingPlans ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <Grid container spacing={2}>
                        {availablePlans.map((plan) => (
                            <Grid item xs={12} sm={6} key={plan.internalId}>
                                <Paper
                                    elevation={3}
                                    style={{
                                        padding: '20px',
                                        cursor: 'pointer',
                                        border: plan.catalogId === subscription?.planId ? '2px solid #17d703' : 'none',
                                        backgroundColor: plan.catalogId === subscription?.planId ? '#f8fff8' : 'white'
                                    }}
                                    onClick={() => handlePlanSelection(plan)}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="h6" style={{ color: '#0404fc' }}>
                                            {plan.title}
                                        </Typography>
                                        {plan.catalogId === subscription?.planId && (
                                            <Typography variant="body2" style={{ color: '#17d703' }}>
                                                Current Plan
                                            </Typography>
                                        )}
                                    </div>
                                    
                                    <Typography variant="h5" style={{ margin: '10px 0' }}>
                                        {plan.price}
                                        <Typography component="span" variant="body2" color="textSecondary">
                                            {' '}{plan.priceDetail}
                                        </Typography>
                                    </Typography>

                                    <Typography variant="body2" color="textSecondary" style={{ minHeight: '40px' }}>
                                        {plan.minimumFee}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenPlanDialog(false)} color="primary">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );

    const renderConfirmationDialog = () => (
        <Dialog
            open={confirmDialogOpen}
            onClose={() => setConfirmDialogOpen(false)}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                Confirm Plan Change
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" gutterBottom>
                    Are you sure you want to change your subscription to the following plan?
                </Typography>
                {selectedPlan && (
                    <Paper elevation={1} style={{ padding: '20px', marginTop: '16px' }}>
                        <Typography variant="h6" style={{ color: '#0404fc' }}>
                            {selectedPlan.title}
                        </Typography>
                        <Typography variant="h5" style={{ margin: '10px 0' }}>
                            {selectedPlan.price}
                            <Typography component="span" variant="body2" color="textSecondary">
                                {' '}{selectedPlan.priceDetail}
                            </Typography>
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {selectedPlan.minimumFee}
                        </Typography>
                    </Paper>
                )}
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={() => setConfirmDialogOpen(false)} 
                    color="primary"
                    disabled={changingPlan}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirmPlanChange}
                    color="primary"
                    variant="contained"
                    disabled={changingPlan}
                    style={{ backgroundColor: changingPlan ? undefined : '#17d703' }}
                >
                    {changingPlan ? (
                        <CircularProgress size={24} style={{ color: 'white' }} />
                    ) : (
                        'Confirm Change'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <div className={classes.pageContainer}>
            <div style={{ backgroundColor: "#0404fc", height: "24px" }} />
            <div className={classes.dashboardContainer}>
                {/* Left Navigation */}
                <div className={classes.leftNav}>
                    <Typography variant="h6" style={{ padding: '16px', color: '#0404fc' }}>
                        Parslii Dashboard
                    </Typography>
                    <List>
                        {navigationItems.map((item) => (
                            <ListItem
                                button
                                key={item.id}
                                className={clsx(classes.navItem, {
                                    [classes.activeNavItem]: activeSection === item.id
                                })}
                                onClick={() => setActiveSection(item.id)}
                            >
                                <ListItemIcon className={classes.navIcon}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText primary={item.label} />
                            </ListItem>
                        ))}
                    </List>
                </div>

                {/* Main Content */}
                <div className={classes.mainContent}>
                    {activeSection === 'dashboard' && (
                        <>
                            <Typography variant='h4' gutterBottom>
                                Welcome {props.userState.user.firstName}!
                            </Typography>
                            {renderSubscriptionInfo()}
                            {Object.entries(groupedProperties).map(([status, properties], index, array) => (
                                <div key={status} className={classes.statusSection}>
                                    <div className={classes.statusHeading}>
                                        {renderStatusIcon(Number(status))}
                                        <Typography variant="h6">
                                            {ClaimStatus[Number(status)] === "Rejected" ? "Updates Required" : ClaimStatus[Number(status)]} Claims ({properties.length})
                                        </Typography>
                                    </div>

                                    <List>
                                        {properties.map(({ property: p }, i: number) => {
                                            const address = p.addresses[0].address + ", " + p.addresses[0].city + ", " +
                                                p.addresses[0].state + " " + p.addresses[0].zip;
                                            const url = streetViewUrls[propertiesOwned.findIndex(po => po.property.internalId === p.internalId)];

                                            return (
                                                <Card key={i} className={classes.propertyCard}>
                                                    <CardActionArea onClick={() => handleCardClick(p, properties[i].claim)}>
                                                        <div className={classes.cardContent}>
                                                            <img
                                                                className={classes.propertyImage}
                                                                alt="Property"
                                                                src={url}
                                                            />
                                                            <div style={{ display: "block", paddingLeft: "15px", flexGrow: 1 }}>
                                                                <Typography variant='body1'>{address}</Typography>
                                                                <Typography style={{ color: "#17d703" }} variant='body1'>
                                                                    <strong>Parslii Score: {(Math.round(p.calcScore)).toString()}</strong>
                                                                </Typography>
                                                            </div>
                                                        </div>
                                                    </CardActionArea>
                                                </Card>
                                            );
                                        })}
                                    </List>

                                    {/* Add divider if not the last section */}
                                    {index < array.length - 1 && (
                                        <Divider className={classes.sectionDivider} /> 
                                    )}
                                </div>
                            ))}
                        </>
                    )}
                    
                    {activeSection === 'properties' && (
                        <div>
                            {/* Properties specific view */}
                            {Object.entries(groupedProperties).map(([status, properties], index, array) => (
                                <div key={status} className={classes.statusSection}>
                                    <div className={classes.statusHeading}>
                                        {renderStatusIcon(Number(status))}
                                        <Typography variant="h6">
                                            {ClaimStatus[Number(status)] === "Rejected" ? "Updates Required" : ClaimStatus[Number(status)]} Claims ({properties.length})
                                        </Typography>
                                    </div>

                                    <List>
                                        {properties.map(({ property: p }, i: number) => {
                                            const address = p.addresses[0].address + ", " + p.addresses[0].city + ", " +
                                                p.addresses[0].state + " " + p.addresses[0].zip;
                                            const url = streetViewUrls[propertiesOwned.findIndex(po => po.property.internalId === p.internalId)];

                                            return (
                                                <Card key={i} className={classes.propertyCard}>
                                                    <CardActionArea onClick={() => handleCardClick(p, properties[i].claim)}>
                                                        <div className={classes.cardContent}>
                                                            <img
                                                                className={classes.propertyImage}
                                                                alt="Property"
                                                                src={url}
                                                            />
                                                            <div style={{ display: "block", paddingLeft: "15px", flexGrow: 1 }}>
                                                                <Typography variant='body1'>{address}</Typography>
                                                                <Typography style={{ color: "#17d703" }} variant='body1'>
                                                                    <strong>Parslii Score: {(Math.round(p.calcScore)).toString()}</strong>
                                                                </Typography>
                                                            </div>
                                                        </div>
                                                    </CardActionArea>
                                                </Card>
                                            );
                                        })}
                                    </List>

                                    {/* Add divider if not the last section */}
                                    {index < array.length - 1 && (
                                        <Divider className={classes.sectionDivider} /> 
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {activeSection === 'subscription' && (
                        <div>
                            {renderSubscriptionInfo()}
                        </div>
                    )}
                    
                    {/* Add other sections as needed */}
                </div>

                {/* Existing dialogs */}
                <LandlordClaimsView
                    open={openDialog}
                    onClose={() => {
                        setOpenDialog(false);
                        setSelectedClaim(null);
                    }}
                    claim={selectedClaim}
                    onClaimUpdated={handleClaimUpdated}
                    questions={[] as Question[]}
                    answers={[] as Answer[]}
                />
                <Backdrop className={classes.backdrop} open={loading}>
                    <div className={classes.loadingContainer}>
                        <CircularProgress color="inherit" size={60} />
                        <Typography className={classes.loadingText} variant="h6">
                            Loading Claim Details...
                        </Typography>
                    </div>
                </Backdrop>
                {renderPlanSelectionDialog()}
                {renderConfirmationDialog()}
            </div>
        </div>
    );
}

const mapStateToProps = (state: any) => ({
    userState: state.userState

});
const mapDispatchToProps = (dispatch: any) => ({
    refreshSession: () => dispatch(refreshSession())
});

export default connect(mapStateToProps, mapDispatchToProps)(LandlordDashboard)
